import type {
  ISbError,
  ISbResult,
  ISbRichtext,
  ISbStoryData,
} from "storyblok-js-client";

export const getStoryBlokData = async () => {
  const route = useRoute();
  const currentRoute = { path: route.path, query: route.query };
  const isPreview = !!(
    currentRoute.query._storyblok && currentRoute.query._storyblok !== ""
  );
  const version = isPreview ? "draft" : "published";
  const storyblokApiInstance = useStoryblokApi();
  currentRoute.path = currentRoute.path.replace(/(^\/+|\/+$)/gm, "");
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `${currentRoute.path}-asyncdata`,
    () =>
      storyblokApiInstance.get(`cdn/stories/${currentRoute.path}`, {
        version: version,
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value?.message ||
        "Something went wrong when fetching from storyblok.",
    });
  } else if (!data.value) {
    throw createError({
      statusCode: 404,
      statusMessage: "Page not found",
    });
  }
  return data.value?.data.story;
};

export const getStoryByPathBlokData = async (slug: string) => {
  const storyblokApiInstance = useStoryblokApi();
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `${slug}-asyncdata`,
    () =>
      storyblokApiInstance.get(`cdn/stories/${slug}`, {
        version: "published",
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value?.message ||
        "Something went wrong when fetching from storyblok.",
    });
  } else if (!data.value) {
    throw createError({
      statusCode: 404,
      statusMessage: "Page not found",
    });
  }
  return data.value?.data.story;
};

export const getStoryblokDataUUID = async (uuid: string) => {
  const storyblokApiInstance = useStoryblokApi();
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `${uuid}-author`,
    async () =>
      await storyblokApiInstance.get("cdn/stories/", {
        version: "published",
        by_uuids: uuid,
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value?.message ||
        "Something went wrong when fetching from storyblok.",
    });
  }

  const content: ISbStoryData[] = [];
  data.value?.data.stories.forEach((el: ISbStoryData) => content.push(el));

  return content;
};

export const getMultiStoryBlokData = async (
  slug: string | undefined,
  sort_by: string = "content.publishedDate:desc",
  exclude: string = "",
) => {
  const storyblokApiInstance = useStoryblokApi();
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `${slug}-all-articles`,
    async () =>
      await storyblokApiInstance.get("cdn/stories", {
        version: "published",
        starts_with: slug,
        per_page: 100,
        page: 1,
        sort_by: sort_by,
        excluding_slugs: exclude,
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value?.message ||
        "Something went wrong when fetching from storyblok.",
    });
  }
  const maxPage = Math.ceil(data.value!.total / data.value!.perPage);
  const contentRequests = [];
  for (let page = 1; page <= maxPage; page++) {
    const {
      data: { stories },
    } = await storyblokApiInstance.get(`cdn/stories/`, {
      version: "published",
      starts_with: slug,
      per_page: data.value?.perPage,
      page: page,
      excluding_slugs: exclude,
    });
    contentRequests.push(...stories);
  }
  return contentRequests;
};

export const getAuthorStoryBlokData = async (
  slug: string | undefined,
  authorUUID: string | undefined,
  sort_by: string = "content.publishedDate:desc",
) => {
  const route = useRoute();
  const currentRoute = { path: route.path, query: route.query };
  const isPreview = !!(
    currentRoute.query._storyblok && currentRoute.query._storyblok !== ""
  );
  const version = isPreview ? "draft" : "published";
  const storyblokApiInstance = useStoryblokApi();
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `${slug}-all-articles`,
    async () =>
      await storyblokApiInstance.get("cdn/stories", {
        version,
        starts_with: slug,
        filter_query: {
          __or: [
            { author: { in: authorUUID } },
            { author: { any_in_array: authorUUID } },
          ],
          hideAuthor: { is: false },
        },
        per_page: 100,
        page: 1,
        sort_by: sort_by,
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value.message ||
        "Something went wrong when fetching from storyblok.",
    });
  }
  const maxPage = Math.ceil(data.value!.total / data.value!.perPage);
  const contentRequests = [];
  for (let page = 1; page <= maxPage; page++) {
    const {
      data: { stories },
    } = await storyblokApiInstance.get(`cdn/stories/`, {
      version: "published",
      starts_with: slug,
      filter_query: {
        __or: [
          { author: { in: authorUUID } },
          { author: { any_in_array: authorUUID } },
        ],
        hideAuthor: { is: false },
      },
      per_page: data.value?.perPage,
      page: page,
    });
    contentRequests.push(...stories);
  }
  return contentRequests;
};

export const getRelatedBlogStoryBlokData = async (
  slug: string,
  tagArray: string[] | undefined,
  currentStory: string | undefined,
  sort_by: string = "content.publishedDate:desc",
) => {
  const route = useRoute();
  const currentRoute = { path: route.path, query: route.query };
  const isPreview = !!(
    currentRoute.query._storyblok && currentRoute.query._storyblok !== ""
  );
  const version = isPreview ? "draft" : "published";
  const storyblokApiInstance = useStoryblokApi();
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `related-blog-all-articles`,
    async () =>
      await storyblokApiInstance.get("cdn/stories", {
        version,
        starts_with: slug,
        filter_query: { tags: { any_in_array: tagArray?.join(",") } },
        per_page: 20,
        page: 1,
        sort_by: sort_by,
        excluding_slugs: currentStory,
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value?.message ||
        "Something went wrong when fetching from storyblok.",
    });
  }
  return data.value?.data.stories;
};

export const getMultiDataSource = async (dataSourceInput: string) => {
  const storyblokApiInstance = useStoryblokApi();
  const { data, error } = await useAsyncData<ISbResult, ISbError>(
    `${dataSourceInput}-all-dataSource`,
    async () =>
      await storyblokApiInstance.get("cdn/datasource_entries", {
        per_page: 100,
        page: 1,
        datasource: dataSourceInput,
      }),
  );

  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode,
      statusMessage:
        error.value?.message ||
        "Something went wrong when fetching from storyblok.",
    });
  }
  const maxPage = Math.ceil(data.value!.total / data.value!.perPage);
  const contentRequests = [];
  for (let page = 1; page <= maxPage; page++) {
    const {
      data: { datasource_entries },
    } = await storyblokApiInstance.get(`cdn/datasource_entries/`, {
      per_page: data.value?.perPage,
      page: page,
      datasource: dataSourceInput,
    });
    contentRequests.push(...datasource_entries);
  }
  return contentRequests;
};

export const sbDescriptionGenerator = (articleContent: ISbRichtext) => {
  let description = "";
  let total = 0;

  for (const content of articleContent.content || []) {
    if (
      (content.type === "paragraph" || content.type === "heading") &&
      total < 3 &&
      content.content?.[0].text !== undefined
    ) {
      content.content?.forEach((item) => {
        if (item.type && item.type === "image") {
          description += ` ${item.attrs.alt}`;
        } else {
          description += ` ${item.text}`;
          total++;
        }
      });
    } else if (
      (content.type === "ordered_list" || content.type === "bullet_list") &&
      total < 3
    ) {
      for (const listItem of content.content!) {
        if (listItem.content?.[0].content?.[0].text !== undefined) {
          description += ` ${listItem.content?.[0].content?.[0].text}`;
          total++;
        }
      }
    }
  }

  return description;
};

export const sbArticleContentSanitiser = (articleContent: ISbRichtext) => {
  let description = "";
  for (const content of articleContent.content || []) {
    if (
      content.type === "paragraph" ||
      (content.type === "heading" && content.content?.[0].text !== undefined)
    ) {
      content.content?.forEach((item) => {
        if (
          item.type &&
          item.type === "image" &&
          item.attrs.alt !== undefined
        ) {
          description += ` ${item.attrs.alt}`;
        } else {
          description += ` ${item.text}`;
        }
      });
    } else if (
      content.type === "ordered_list" ||
      content.type === "bullet_list"
    ) {
      for (const listItem of content.content!) {
        if (listItem.content?.[0].content?.[0].text !== undefined) {
          description += ` ${listItem.content?.[0].content?.[0].text}`;
        }
      }
    }
  }

  return description;
};
